/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { useEffect, useRef, useState } from 'react'
import { IoInformationCircle } from 'react-icons/io5'

import { Tooltip } from 'src/components/atoms/Tooltip'

import { dateToInputValues, inputValuesToDate } from './utils'

export interface DateTimePickerProps {
  className?: string
  onChange: (e: Date) => void
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  value: Date | string | undefined
}

export const DateTimePicker = ({
  className = '',
  onChange,
  onBlur,
  value
}: DateTimePickerProps) => {
  const initialValues = dateToInputValues(
    typeof value === 'string' ? new Date(value) : value
  )

  const [date, setDate] = useState(initialValues.date)
  const [time, setTime] = useState(initialValues.time)
  const isIncompleteInputRef = useRef(false)

  useEffect(() => {
    const dateValue = typeof value === 'string' ? new Date(value) : value

    // Don't reset local state if we have incomplete input and parent sent null
    if (!dateValue && isIncompleteInputRef.current) {
      isIncompleteInputRef.current = false
      return
    }

    if (
      !dateValue ||
      (dateValue instanceof Date && dateValue.toString() !== 'Invalid Date')
    ) {
      const newValues = dateToInputValues(dateValue)
      setDate(newValues.date)
      setTime(newValues.time)
    }
  }, [value])

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value
    setDate(newDate)

    if (newDate && time) {
      const combinedDate = inputValuesToDate(newDate, time)
      if (combinedDate.toString() !== 'Invalid Date') {
        onChange(combinedDate)
      }
    }
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value
    setTime(newTime)

    if (date && newTime) {
      const combinedDate = inputValuesToDate(date, newTime)
      if (combinedDate.toString() !== 'Invalid Date') {
        onChange(combinedDate)
      }
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if ((date && !time) || (!date && time)) {
      isIncompleteInputRef.current = true
      onChange(null as any)
    }
    onBlur(e)
  }

  return (
    <div className={`flex flex-col md:flex-row w-full gap-4 pt-2 ${className}`}>
      <div className='flex flex-col md:w-auto'>
        <label
          htmlFor='datetime-date-input'
          className='text-sm font-normal text-steelGrey mb-2'
        >
          Date
        </label>
        <input
          id='datetime-date-input'
          type='date'
          aria-label='date'
          role='textbox'
          className='form-input-wrapper bg-white w-full md:w-auto'
          onChange={handleDateChange}
          onBlur={handleBlur}
          value={date}
        />
      </div>
      <div className='flex flex-col'>
        <label
          htmlFor='datetime-time-input'
          className='text-sm font-normal text-steelGrey mb-2 flex items-center gap-1'
        >
          Time
          <Tooltip
            placement='top'
            overlay={
              <div className='text-xs max-w-[250px]'>
                Click the clock icon to select a time, or type it directly using
                12-hour format (e.g., 2:30 PM).
              </div>
            }
          >
            <IoInformationCircle className='text-base text-ashGrey cursor-help' />
          </Tooltip>
        </label>
        <input
          id='datetime-time-input'
          type='time'
          aria-label='time'
          role='textbox'
          className='form-input-wrapper bg-white w-full md:w-auto md:max-w-fit'
          onChange={handleTimeChange}
          onBlur={handleBlur}
          value={time}
          placeholder='Select time'
        />
      </div>
    </div>
  )
}
